import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { BinocularsIcon } from '@phosphor-icons/react'
import { useMediaQuery, Box } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { RowBox } from '@components/shared/RowBox'
import { setInitialValues } from './form/setInitialValues'
import { SeparatorWithLine } from '@components/shared/SeparatorWithLine'
import { usePreview } from '@pages/Preview/context'

import type { AddRditOtherConsumptionsModalProps } from './types'

import * as S from './styles'

export const SeeReturnDetailsModal = ({ data, open, onClose, ...rest }: AddRditOtherConsumptionsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { getReturnPreview, periodSelectedData } = usePreview()

  const methods = useForm<any>({
    defaultValues: initialFormValues
  })

  const { fields } = useFieldArray({
    control: methods.control,
    name: 'accounts'
  })

  useEffect(() => {
    if (!data || !open) return

    const branchCode = data.profitCenter ?? ''
    const weekNumber = Number(data.weekNumber) ?? 0
    const referenceDate = periodSelectedData?.competenceDate ?? ''

    if (!branchCode || !referenceDate) {
      methods.reset(setInitialValues(data))
      return
    }

    getReturnPreview({ branchCode, weekNumber, referenceDate }).then((returnData) => {
      if (returnData?.items?.length) {
        methods.reset(setInitialValues(data, returnData.items))
      } else {
        methods.reset(setInitialValues(data))
      }
    })
  }, [data, open, periodSelectedData?.competenceDate])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
    }
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        title="Ver detalhes Devolução"
        headerIcon={<BinocularsIcon />}
        onClose={onClose}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5
        }}
        {...rest}
      >
        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={2}>
              <Input name="economicGroup" label="Grupo Econômico" disabled />
              <Input name="profitCenter" label="Centro de Lucro" disabled />
            </RowBox>

            <RowBox columns={3}>
              <Input name="budget" label="Budget GP (R$)" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input
                name="actualPreview"
                label="Prévia Atual GP (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
              <Input
                name="deviationVsBudget"
                label="Desvio vs Budget GP (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
            </RowBox>

            {fields.map((field, index) => (
              <S.InputsBox key={field.id}>
                {!!index && <SeparatorWithLine marginBottom="24px" />}

                <RowBox columns={3}>
                  <Input name={`accounts.${index}.account`} label="Conta" disabled />
                  <Box sx={{ gridColumn: 'span 2' }}>
                    <Input
                      name={`accounts.${index}.returnJustification`}
                      label="Justificativa da devolução"
                      disabled
                    />
                  </Box>
                  <Input
                    name={`accounts.${index}.userAndProfile`}
                    label="Usuário e Perfil resp. devolução"
                    disabled
                  />
                  <Input
                    name={`accounts.${index}.dateAndTime`}
                    label="Data e hora devolução"
                    maskType="date"
                    placeholder="dd/mm/yyyy"
                    disabled
                  />
                  <Input
                    name={`accounts.${index}.validationPossibleUntil`}
                    label="Validação possível até"
                    maskType="date"
                    placeholder="dd/mm/yyyy"
                    tooltipInfo="Esta é a data limite para que o perfil que devolveu a prévia possa validá-la novamente. Retorne a devolução com antecedência para evitar atrasos no envio da prévia."
                    disabled
                  />
                </RowBox>
              </S.InputsBox>
            ))}
          </S.Form>
        </FormProvider>
      </BaseModal2>
    </>
  )
}
